import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, X, FileText, CurrencyInr } from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';
import { TenderOwnerPicker } from '../components/TenderOwnerPicker';
import { useTenantPath } from '../lib/tenantPath';
import PrevNextNav from '../components/PrevNextNav';

import { notify } from '../lib/notify';
import { PageSkeleton } from '../components/Skeletons';
import { EmptyState } from '../components/EmptyState';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHECKS = [
  { key: 'product_match', label: 'Product match (specs)' },
  { key: 'certificates', label: 'Certificates (OEM / ISO / CE)' },
  { key: 'financials', label: 'Financials (turnover, past supply)' },
  { key: 'emd_ready', label: 'EMD / BG readiness' },
  { key: 'delivery_capable', label: 'Delivery + service coverage' },
  { key: 'margin_acceptable', label: 'Commercial margin' },
];
const TERNARY = ['yes', 'no', 'needs_input'];

// iter96 — Participation setup presets. Must match backend routes_tenders._*_ALLOWED sets.
const PAYMENT_TERMS = ['Advance', 'Milestone', 'Net-30', 'Net-60', 'Custom'];
const DELIVERY_TERMS = ['FOR Destination', 'Ex-Works', 'CIF', 'DAP', 'Custom'];
const DELIVERY_MODES = ['Single Location', 'Multi Location'];
const PARTICIPATION_METHODS = ['Direct', 'Through Dealer', 'With Liaison'];

/** Tender detail — Feb 2026 (iter89). Compact panel with feasibility check
 *  editor, EMD editor, submission + outcome actions all inline. */
export default function TenderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tp = useTenantPath();
  const { user, token: authToken } = useAuthStore();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [feasEdit, setFeasEdit] = useState(false);
  const [feas, setFeas] = useState(null);
  const [emdEdit, setEmdEdit] = useState(false);
  const [emd, setEmd] = useState(null);

  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API}/tenders`).then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  // iter132.12 — Slot-specific upload for the "missing" pills on the docs
  // panel. Uses the existing /upload-doc + /documents flow so RBAC + object
  // storage are already handled. `uploadingSlot` guards double-clicks.
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const handleSlotUpload = async (slotKey, slotLabel, file) => {
    if (!file) return;
    setUploadingSlot(slotKey);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await axios.post(`${API}/tenders/upload-doc`, fd);
      await axios.post(`${API}/tenders/${id}/documents`, {
        name: up.data.filename || file.name,
        url: up.data.file_url,
        category: slotKey,
      });
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
        : (typeof detail === 'string' ? detail : (e.message || 'Upload failed'));
      notify(`Failed to upload ${slotLabel}:\n\n${msg}`);
    } finally {
      setUploadingSlot(null);
    }
  };
  const navItems = list.map((x) => ({ id: x.id, label: `${x.tender_number} · ${x.buyer_name}` }));

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const r = await axios.get(`${API}/tenders/${id}`);
      setT(r.data);
      setFeas(r.data.feasibility_checks || { notes: '' });
      setEmd(r.data.emd || {});
    } catch (e) {
      // iter132.10 — surface the real error instead of a bare "Failed to load".
      // FastAPI 422 arrives as an array under `detail` — stringify defensively.
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
        : (typeof detail === 'string' ? detail : (e.message || 'Failed to load'));
      setLoadError(msg);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loadError && !t) {
    return (
      <div className="p-8">
        <div className="medical-card">
          <EmptyState title="Tender not available" hint={loadError} testid="tender-load-error"
            action={<button onClick={() => navigate(tp('/tenders'))} className="px-4 py-2 rounded-lg bg-[#00a69c] text-white text-sm font-semibold">Back to Tenders</button>} />
        </div>
      </div>
    );
  }
  if (loading || !t) return <PageSkeleton />;

  const isManager = ['Regional Sales Manager', 'National Sales Manager', 'Vice President', 'President'].includes(user?.role);
  // TE rule: owner TE only when assigned, otherwise any TE. Manager+ always.
  const teCanWork = user?.role === 'Tender Executive' && (!t.owner_te_id || t.owner_te_id === user?.id);
  const canWork = isManager || teCanWork;
  const canUploadDocs = canWork;
  const canApprove = ['Regional Sales Manager', 'National Sales Manager', 'Vice President', 'President'].includes(user?.role);

  const doAction = async (path, body = {}, params = {}) => {
    try {
      await axios.post(`${API}/tenders/${id}/${path}`, body, { params });
      await load();
    } catch (e) {
      notify(e.response?.data?.detail || 'Action failed');
    }
  };

  const submitFeas = async () => { await doAction('feasibility-submit', { feasibility_checks: feas }); setFeasEdit(false); };
  const decideFeas = async (decision) => {
    let reason = '';
    if (decision === 'reject') {
      reason = window.prompt('Rejection reason (required):', '') || '';
      if (!reason.trim()) return;
    }
    await doAction('feasibility-decision', { decision, reason });
  };
  const saveEmd = async () => {
    try { await axios.put(`${API}/tenders/${id}/emd`, emd); setEmdEdit(false); await load(); }
    catch (e) { notify(e.response?.data?.detail || 'EMD save failed'); }
  };
  const submitBid = async () => {
    const dsc = window.prompt('DSC identifier used:', '') || '';
    const rcpt = window.prompt('Bid receipt / acknowledgement number:', '') || '';
    await doAction('submit', {}, { dsc_used: dsc, bid_receipt_number: rcpt });
  };
  const recordOutcome = async (outcome) => {
    let body = { outcome };
    if (outcome === 'awarded') {
      body.award_po_number = window.prompt('PO number:', '') || '';
      body.award_po_value = parseFloat(window.prompt('PO value (₹):', '') || '0');
      body.award_po_date = window.prompt('PO date (YYYY-MM-DD):', '') || '';
    } else if (outcome === 'not_selected') {
      body.loss_reason = window.prompt('Reason we lost:', '') || '';
      body.winner_name = window.prompt('Winner name (optional):', '') || '';
      body.winner_price = parseFloat(window.prompt('Winner price (₹, optional):', '') || '0');
    }
    await doAction('outcome', body);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="tender-detail">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(tp('/tenders'))} className="w-10 h-10 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded-lg" data-testid="back-button">
          <ArrowLeft size={24} />
        </button>
        <PrevNextNav items={navItems} currentId={id} onNavigate={(nid) => navigate(tp(`/tenders/${nid}`))} testIdPrefix="tender" />
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>{t.tender_number}</h1>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full uppercase bg-teal-100 text-teal-800">{(t.status || '').replace(/_/g, ' ')}</span>
          </div>
          <p className="text-[#6D8E87]">{t.buyer_name} · {t.source} · {t.tender_ref_id}</p>
        </div>
      </div>

      {/* iter96 — 4-step progress bar */}
      <TenderStepper t={t} onJump={(idAttr) => {
        const el = document.querySelector(`[data-testid="${idAttr}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Panel title="Timeline">
          <Kv k="Publish" v={t.publish_date || '—'} />
          <Kv k="Deadline" v={t.submission_deadline || '—'} />
          <Kv k="Opening" v={t.opening_date || '—'} />
          <Kv k="Est. Value" v={`₹${Number(t.estimated_value || 0).toLocaleString('en-IN')}`} />
        </Panel>
        <Panel title="Team">
          <Kv k="Created by" v={t.created_by_name || '—'} />
          <Kv k="Paired SE" v={t.paired_sales_executive_name || '— unpaired —'} />
          <TenderOwnerPicker tender={t} user={user} onSaved={load} />
          <Kv k="Approver role" v={t.feasibility_approver_role || '—'} />
          <Kv k="Approver" v={t.feasibility_approver_name || (t.status === 'feasibility_pending' ? 'pending' : '—')} />
        </Panel>
        <Panel title="EMD">
          {t.emd?.required ? (
            <>
              <Kv k="Amount" v={`₹${Number(t.emd.amount || 0).toLocaleString('en-IN')}`} />
              <Kv k="Mode" v={t.emd.mode || '—'} />
              <Kv k="Bank / Ref" v={`${t.emd.bank || '—'} · ${t.emd.ref_number || ''}`} />
              <Kv k="Validity" v={t.emd.validity_date || '—'} />
              <Kv k="Status" v={t.emd.status || 'pending'} />
            </>
          ) : <div className="text-sm text-[#6D8E87]">Not required</div>}
          {canWork && <button onClick={() => setEmdEdit(true)} className="mt-2 text-xs text-[#00a69c] font-semibold" data-testid="tender-edit-emd">Edit EMD →</button>}
        </Panel>
      </div>

      {/* Feasibility */}
      <div className="medical-card p-4 mb-6" data-testid="tender-feasibility-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#052A23]">Feasibility check</h3>
          <div className="flex gap-2">
            {canWork && (t.status === 'draft' || t.status === 'no_go') && !feasEdit && (
              <button onClick={() => setFeasEdit(true)} className="text-xs px-3 py-1 rounded border border-[#00a69c] text-[#00a69c]" data-testid="tender-edit-feas">Edit & submit</button>
            )}
            {canApprove && t.status === 'feasibility_pending' && (
              <>
                <button onClick={() => decideFeas('approve')} className="text-xs px-3 py-1 rounded bg-emerald-600 text-white font-semibold" data-testid="tender-feas-approve">Approve → Go Ahead</button>
                <button onClick={() => decideFeas('reject')} className="text-xs px-3 py-1 rounded bg-rose-600 text-white font-semibold" data-testid="tender-feas-reject">Reject → No Go</button>
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CHECKS.map((c) => (
            <div key={c.key} className="flex items-center justify-between text-sm">
              <span className="text-[#3B5A54]">{c.label}</span>
              {feasEdit ? (
                <select value={feas?.[c.key] || 'needs_input'} onChange={(e) => setFeas((f) => ({ ...f, [c.key]: e.target.value }))} className="text-xs px-2 py-1 border rounded" data-testid={`tender-feas-${c.key}`}>
                  {TERNARY.map((v) => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
                </select>
              ) : (
                <span className={`text-xs font-semibold ${
                  t.feasibility_checks?.[c.key] === 'yes' ? 'text-emerald-700'
                  : t.feasibility_checks?.[c.key] === 'no' ? 'text-rose-700'
                  : 'text-amber-700'
                }`}>{(t.feasibility_checks?.[c.key] || 'needs input').replace(/_/g, ' ')}</span>
              )}
            </div>
          ))}
        </div>
        {feasEdit && (
          <div className="mt-3">
            <textarea placeholder="Notes …" value={feas?.notes || ''} onChange={(e) => setFeas((f) => ({ ...f, notes: e.target.value }))} className="w-full p-2 border rounded text-sm" rows={2} data-testid="tender-feas-notes" />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => { setFeas(t.feasibility_checks || {}); setFeasEdit(false); }} className="text-xs px-3 py-1 border rounded">Cancel</button>
              <button onClick={submitFeas} className="text-xs px-3 py-1 bg-[#00a69c] text-white rounded font-semibold" data-testid="tender-feas-submit">Submit for approval</button>
            </div>
          </div>
        )}
        {t.feasibility_reason && (
          <div className="mt-3 text-xs text-[#6D8E87] italic">Reason: {t.feasibility_reason}</div>
        )}
      </div>

      {/* iter96 — Participation setup (Step 2) — inline accordion. Locked
          until feasibility is approved (status = go_ahead). TE-only write. */}
      <ParticipationSection t={t} user={user} onSaved={load} />

      {/* Actions bar */}
      {canWork && ['go_ahead', 'submitted', 'tech_qualified'].includes(t.status) && (
        <div className="medical-card p-4 mb-6 flex flex-wrap gap-2" data-testid="tender-actions-bar">
          {t.status === 'go_ahead' && (
            <button
              onClick={submitBid}
              disabled={!t.participation_locked}
              title={t.participation_locked ? '' : 'Complete Step 2 (Participation) first'}
              className={`text-sm px-3 py-1.5 rounded font-semibold ${t.participation_locked ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              data-testid="tender-submit-bid"
            >Mark Submitted</button>
          )}
          {['submitted', 'tech_qualified'].includes(t.status) && (
            <>
              <button onClick={() => recordOutcome('tech_qualified')} className="text-sm px-3 py-1.5 bg-blue-500 text-white rounded" data-testid="tender-outcome-tech-qualified">Tech Qualified</button>
              <button onClick={() => recordOutcome('tech_disqualified')} className="text-sm px-3 py-1.5 bg-rose-500 text-white rounded" data-testid="tender-outcome-tech-dq">Tech Disqualified</button>
              <button onClick={() => recordOutcome('awarded')} className="text-sm px-3 py-1.5 bg-emerald-600 text-white rounded font-semibold" data-testid="tender-outcome-awarded">Awarded 🎉</button>
              <button onClick={() => recordOutcome('not_selected')} className="text-sm px-3 py-1.5 bg-rose-600 text-white rounded" data-testid="tender-outcome-lost">Not Selected</button>
              <button onClick={() => recordOutcome('withdrawn')} className="text-sm px-3 py-1.5 bg-gray-500 text-white rounded" data-testid="tender-outcome-withdrawn">Withdrawn</button>
            </>
          )}
        </div>
      )}

      {t.linked_lead_id && (
        <div className="medical-card p-4 mb-6 border-l-4 border-emerald-500" data-testid="tender-linked-lead">
          <div className="text-sm text-[#052A23]">
            <CheckCircle size={16} weight="fill" className="inline text-emerald-500 mr-1" />
            Awarded — linked Lead created. <button onClick={() => navigate(tp(`/leads/${t.linked_lead_id}`))} className="text-[#00a69c] font-semibold underline" data-testid="tender-open-lead">Open Lead →</button>
          </div>
        </div>
      )}

      {/* Discovery flags + geo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="medical-card p-4" data-testid="tender-geo-panel">
          <h3 className="font-semibold text-[#052A23] mb-2">Region & flags</h3>
          <div className="space-y-1">
            <Kv k="Region" v={t.region || '—'} />
            <Kv k="State" v={t.state || '—'} />
            <div className="flex flex-wrap gap-2 pt-2">
              <Flag on={t.reverse_auction} label="Reverse Auction" testId="tender-flag-ra" />
              <Flag on={t.emd_exemption} label="EMD Exemption" testId="tender-flag-emd-ex" />
              <Flag on={t.rate_contract} label="Rate Contract" testId="tender-flag-rc" />
            </div>
          </div>
        </div>
        <div className="medical-card p-4" data-testid="tender-docs-panel">
          <h3 className="font-semibold text-[#052A23] mb-2">Documents</h3>
          <div className="space-y-1.5 text-sm">
            {[
              ['tender_doc', 'Tender Document'],
              ['emd_page', 'EMD Page'],
              ['participation_criteria', 'Participation Criteria'],
              ['boq_sheet', 'BOQ Sheet'],
            ].map(([cat, label]) => {
              const doc = (t.documents || []).find((d) => d.category === cat);
              return (
                <div key={cat} className="flex items-center justify-between gap-2" data-testid={`tender-doc-row-${cat}`}>
                  <span className="text-[#3B5A54]">{label}</span>
                  {doc ? (
                    <a
                      href={`${BACKEND_URL}${doc.url}${doc.url.includes('?') ? '&' : '?'}auth=${encodeURIComponent(authToken || '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#00a69c] hover:underline text-xs font-semibold truncate max-w-[60%]"
                      title={doc.name}
                    >
                      {doc.name} ↗
                    </a>
                  ) : canUploadDocs ? (
                    <label
                      className={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full transition cursor-pointer ${
                        uploadingSlot === cat
                          ? 'bg-amber-100 text-amber-800 cursor-wait'
                          : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                      }`}
                      title={`Click to upload ${label}`}
                      data-testid={`tender-doc-upload-${cat}`}
                    >
                      {uploadingSlot === cat ? 'uploading…' : 'missing · upload'}
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
                        disabled={!!uploadingSlot}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (f) handleSlotUpload(cat, label, f);
                        }}
                      />
                    </label>
                  ) : (
                    <span className="text-xs text-rose-500 italic">missing</span>
                  )}
                </div>
              );
            })}
            {(t.documents || []).filter((d) => !['tender_doc', 'emd_page', 'participation_criteria', 'boq_sheet'].includes(d.category)).map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 pt-1 border-t border-[#F0F5F4]">
                <span className="text-[#6D8E87] text-xs">Other · {d.name}</span>
                <a
                  href={`${BACKEND_URL}${d.url}${d.url.includes('?') ? '&' : '?'}auth=${encodeURIComponent(authToken || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00a69c] hover:underline text-xs"
                >
                  Open ↗
                </a>
              </div>
            ))}
            {canUploadDocs && (
              <label
                className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border border-dashed transition-colors cursor-pointer ${
                  uploadingSlot === 'other' ? 'border-amber-300 bg-amber-50 text-amber-800 cursor-wait' : 'border-[#00a69c] text-[#00a69c] hover:bg-[#F4F9F7]'
                }`}
                data-testid="tender-doc-upload-other"
              >
                {uploadingSlot === 'other' ? 'Uploading…' : '+ Upload another document'}
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
                  disabled={!!uploadingSlot}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) handleSlotUpload('other', 'document', f);
                  }}
                  data-testid="tender-doc-upload-other-input"
                />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Source URL & Notes — captured in the New Tender form (iter93) but
          previously not surfaced on the detail page (iter95 fix). */}
      {(t.source_url || t.source_notes) && (
        <div className="medical-card p-4 mb-6" data-testid="tender-source-panel">
          <h3 className="font-semibold text-[#052A23] mb-2">Source</h3>
          {t.source_url && (
            <div className="text-sm mb-2">
              <span className="text-[#6D8E87]">Source URL: </span>
              <a
                href={t.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00a69c] hover:underline break-all"
                data-testid="tender-source-url"
              >
                {t.source_url} ↗
              </a>
            </div>
          )}
          {t.source_notes && (
            <div className="text-sm text-[#3B5A54] whitespace-pre-wrap" data-testid="tender-source-notes">
              <span className="text-[#6D8E87]">Notes: </span>{t.source_notes}
            </div>
          )}
        </div>
      )}

      {emdEdit && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <h3 className="font-bold text-[#052A23] mb-3">Edit EMD</h3>
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!emd?.required} onChange={(e) => setEmd((v) => ({ ...v, required: e.target.checked }))} /> Required</label>
              <input placeholder="Amount ₹" type="number" value={emd?.amount || 0} onChange={(e) => setEmd((v) => ({ ...v, amount: parseFloat(e.target.value) || 0 }))} className="w-full p-2 border rounded" />
              <select value={emd?.mode || 'Bank Guarantee'} onChange={(e) => setEmd((v) => ({ ...v, mode: e.target.value }))} className="w-full p-2 border rounded">
                {['Demand Draft', 'Bank Guarantee', 'Online', 'Exempt'].map((m) => <option key={m}>{m}</option>)}
              </select>
              <input placeholder="Bank" value={emd?.bank || ''} onChange={(e) => setEmd((v) => ({ ...v, bank: e.target.value }))} className="w-full p-2 border rounded" />
              <input placeholder="Reference #" value={emd?.ref_number || ''} onChange={(e) => setEmd((v) => ({ ...v, ref_number: e.target.value }))} className="w-full p-2 border rounded" />
              <input type="date" placeholder="Validity" value={emd?.validity_date || ''} onChange={(e) => setEmd((v) => ({ ...v, validity_date: e.target.value }))} className="w-full p-2 border rounded" />
              <select value={emd?.status || 'pending'} onChange={(e) => setEmd((v) => ({ ...v, status: e.target.value }))} className="w-full p-2 border rounded">
                {['pending', 'lodged', 'returned', 'forfeited'].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setEmd(t.emd || {}); setEmdEdit(false); }} className="text-sm px-3 py-1 border rounded">Cancel</button>
              <button onClick={saveEmd} className="text-sm px-3 py-1 bg-[#00a69c] text-white rounded font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Panel = ({ title, children }) => (
  <div className="medical-card p-4">
    <h3 className="font-semibold text-[#052A23] mb-2">{title}</h3>
    <div className="space-y-1">{children}</div>
  </div>
);
const Kv = ({ k, v }) => (
  <div className="flex justify-between text-sm">
    <span className="text-[#6D8E87]">{k}</span>
    <span className="text-[#052A23] font-medium">{v}</span>
  </div>
);
const Flag = ({ on, label, testId }) => (
  <span
    data-testid={testId}
    className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
      on ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'
    }`}
  >
    {on ? '✓ ' : '· '}{label}
  </span>
);

/* ────────────────────────────────────────────────────────────────────────
 * iter96 — Tender 4-step progress bar.
 * Read-only visual on the top of the detail page. Renders the completion
 * state of Feasibility → Participation → Submit → Outcome.
 * ─────────────────────────────────────────────────────────────────────── */
function TenderStepper({ t, onJump }) {
  const feasApproved = ['go_ahead', 'submitted', 'tech_qualified', 'tech_disqualified', 'awarded', 'not_selected', 'withdrawn'].includes(t.status);
  const feasRejected = t.status === 'no_go';
  const participationDone = !!t.participation_locked;
  const submitDone = ['submitted', 'tech_qualified', 'tech_disqualified', 'awarded', 'not_selected'].includes(t.status);
  const outcomeDone = ['awarded', 'not_selected', 'tech_disqualified', 'withdrawn'].includes(t.status);

  const steps = [
    { n: 1, label: 'Feasibility', done: feasApproved, failed: feasRejected, jump: 'tender-feasibility-panel' },
    { n: 2, label: 'Participation', done: participationDone, jump: 'tender-participation-panel', locked: !feasApproved },
    { n: 3, label: 'Submit', done: submitDone, jump: 'tender-actions-bar', locked: !participationDone },
    { n: 4, label: 'Outcome', done: outcomeDone, jump: 'tender-actions-bar', locked: !submitDone },
  ];

  return (
    <div className="medical-card px-4 py-3 mb-4" data-testid="tender-stepper">
      <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <button
              type="button"
              disabled={s.locked}
              onClick={() => !s.locked && onJump(s.jump)}
              title={s.locked ? 'Complete the previous step first' : `Go to ${s.label}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                s.failed ? 'bg-rose-100 text-rose-800' :
                s.done ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' :
                s.locked ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :
                'bg-amber-100 text-amber-800 hover:bg-amber-200'
              }`}
              data-testid={`stepper-step-${s.n}`}
            >
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] ${
                s.done ? 'bg-emerald-600 text-white' :
                s.failed ? 'bg-rose-600 text-white' :
                s.locked ? 'bg-gray-300 text-white' : 'bg-amber-500 text-white'
              }`}>{s.done ? '✓' : s.failed ? '✕' : s.n}</span>
              {s.label}
            </button>
            {i < steps.length - 1 && <span className={`h-0.5 flex-1 min-w-4 ${s.done ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────────
 * iter96 — Participation Setup (Step 2).
 * Read view for everyone; edit form for TE creator (President safety valve).
 * Locked until feasibility is approved. After save it renders read-only
 * with an "Edit" button (still TE-only) so the TE can refine terms before
 * hitting Submit.
 * ─────────────────────────────────────────────────────────────────────── */
function ParticipationSection({ t, user, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aiPrefilled, setAiPrefilled] = useState(false);
  const [form, setForm] = useState({
    participation_method: t.participation_method || 'Direct',
    dealer_customer_id: t.dealer_customer_id || '',
    liaison_customer_id: t.liaison_customer_id || '',
    payment_terms: t.payment_terms || 'Net-30',
    payment_terms_notes: t.payment_terms_notes || '',
    delivery_terms: t.delivery_terms || 'FOR Destination',
    delivery_terms_notes: t.delivery_terms_notes || '',
    delivery_mode: t.delivery_mode || 'Single Location',
    delivery_locations_notes: t.delivery_locations_notes || '',
    bid_validity_days: t.bid_validity_days || 0,
    contract_duration: t.contract_duration || '',
    warranty_period: t.warranty_period || '',
    performance_criteria: t.performance_criteria || '',
    experience_criteria: t.experience_criteria || '',
    other_info: t.participation_other_info || '',
  });

  const feasApproved = t.status === 'go_ahead' ||
    ['submitted', 'tech_qualified', 'tech_disqualified', 'awarded', 'not_selected', 'withdrawn'].includes(t.status);
  const teCanWork = user?.role === 'Tender Executive' && (!t.owner_te_id || t.owner_te_id === user?.id);
  const canEdit = feasApproved && (teCanWork || user?.role === 'President');
  const dealers = customers.filter((c) => (c.customer_role || 'End Buyer') === 'Dealer');
  const liaisons = customers.filter((c) => (c.customer_role || 'End Buyer') === 'Liaison Agent');

  useEffect(() => {
    if (!editing) return;
    axios.get(`${API}/customers`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.customers || []);
      setCustomers(list);
    }).catch(() => setCustomers([]));
    // iter99 — hydrate any AI-extracted step 2 hints from the create-time
    // localStorage stash (keyed on tender_ref_id). Only fills blanks so the
    // TE never loses typed values. Read-once — clear after applying.
    if (!t.participation_locked && t.tender_ref_id) {
      try {
        const key = `tender_ai_step2_${t.tender_ref_id}`;
        const raw = localStorage.getItem(key);
        if (raw) {
          const hint = JSON.parse(raw) || {};
          setForm((prev) => {
            const next = { ...prev };
            const fillIf = (k, v) => {
              if (v === null || v === undefined || v === '') return;
              if (next[k] === '' || next[k] === 0 || next[k] === undefined || next[k] === null) next[k] = v;
            };
            fillIf('payment_terms', hint.payment_terms);
            fillIf('payment_terms_notes', hint.payment_terms_notes);
            fillIf('delivery_terms', hint.delivery_terms);
            fillIf('delivery_terms_notes', hint.delivery_terms_notes);
            fillIf('delivery_mode', hint.delivery_mode);
            fillIf('delivery_locations_notes', hint.delivery_locations_notes);
            fillIf('bid_validity_days', hint.bid_validity_days);
            fillIf('contract_duration', hint.contract_duration);
            fillIf('warranty_period', hint.warranty_period);
            fillIf('performance_criteria', hint.performance_criteria);
            fillIf('experience_criteria', hint.experience_criteria);
            return next;
          });
          setAiPrefilled(true);
        }
      } catch { /* ignore */ }
    }
  }, [editing, t.participation_locked, t.tender_ref_id]);

  const save = async () => {
    if (form.participation_method === 'Through Dealer' && !form.dealer_customer_id) {
      notify('Please pick a Dealer from Customers. Tag a customer with role "Dealer" first if none exist.'); return;
    }
    if (form.participation_method === 'With Liaison' && !form.liaison_customer_id) {
      notify('Please pick a Liaison Agent from Customers. Tag a customer with role "Liaison Agent" first if none exist.'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/tenders/${t.id}/participation`, form);
      setEditing(false);
      onSaved();
    } catch (e) {
      notify(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!feasApproved) {
    return (
      <div className="medical-card p-4 mb-6 opacity-70" data-testid="tender-participation-panel">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#052A23]">Step 2 · Participation Setup</span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase bg-gray-200 text-gray-500">Locked</span>
        </div>
        <p className="text-xs text-[#6D8E87] mt-1">Complete Step 1 (Feasibility) and get manager approval to unlock this step.</p>
      </div>
    );
  }

  const isLocked = !!t.participation_locked;

  return (
    <div className="medical-card p-4 mb-6" data-testid="tender-participation-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-[#052A23]">Step 2 · Participation Setup</h3>
          {isLocked && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase bg-emerald-100 text-emerald-800">Saved</span>}
        </div>
        {canEdit && !editing && (
          <button onClick={() => setEditing(true)} className="text-xs px-3 py-1 rounded border border-[#00a69c] text-[#00a69c] font-semibold" data-testid="tender-participation-edit">
            {isLocked ? 'Edit' : 'Fill in →'}
          </button>
        )}
      </div>

      {!editing && !isLocked && (
        <p className="text-sm text-[#6D8E87] italic">
          {canEdit
            ? 'Not yet filled in. Click "Fill in →" to capture participation method, payment/delivery terms and other bid conditions.'
            : 'The Tender Executive who created this tender will fill this in before the bid is submitted.'}
        </p>
      )}

      {!editing && isLocked && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1 text-sm" data-testid="tender-participation-view">
            <Kv k="Method" v={t.participation_method || '—'} />
            {t.participation_method === 'Through Dealer' && <Kv k="Dealer" v={t.dealer_customer_name || '—'} />}
            {t.participation_method === 'With Liaison' && <Kv k="Liaison Agent" v={t.liaison_customer_name || '—'} />}
            <Kv k="Payment terms" v={t.payment_terms || '—'} />
            <Kv k="Delivery terms" v={t.delivery_terms || '—'} />
            <Kv k="Delivery mode" v={t.delivery_mode || '—'} />
            <Kv k="Bid validity" v={t.bid_validity_days ? `${t.bid_validity_days} days` : '—'} />
            <Kv k="Contract duration" v={t.contract_duration || '—'} />
            <Kv k="Warranty" v={t.warranty_period || '—'} />
            {(t.payment_terms_notes || t.delivery_terms_notes || t.delivery_locations_notes || t.performance_criteria || t.experience_criteria || t.participation_other_info) && (
              <div className="col-span-full mt-2 space-y-1 text-xs text-[#3B5A54]">
                {t.payment_terms_notes && <div><span className="text-[#6D8E87]">Payment notes: </span>{t.payment_terms_notes}</div>}
                {t.delivery_terms_notes && <div><span className="text-[#6D8E87]">Delivery notes: </span>{t.delivery_terms_notes}</div>}
                {t.delivery_locations_notes && <div><span className="text-[#6D8E87]">Locations: </span>{t.delivery_locations_notes}</div>}
                {t.performance_criteria && <div><span className="text-[#6D8E87]">Performance criteria: </span>{t.performance_criteria}</div>}
                {t.experience_criteria && <div><span className="text-[#6D8E87]">Experience criteria: </span>{t.experience_criteria}</div>}
                {t.participation_other_info && <div className="whitespace-pre-wrap"><span className="text-[#6D8E87]">Other: </span>{t.participation_other_info}</div>}
              </div>
            )}
          </div>
          <ParticipationHistory history={t.participation_history} />
        </>
      )}

      {editing && (
        <div className="space-y-4" data-testid="tender-participation-edit-form">
          {aiPrefilled && (
            <div className="px-3 py-2 rounded-lg bg-indigo-50 text-indigo-800 text-xs flex items-center gap-2" data-testid="participation-ai-prefill-banner">
              <span>✨</span> AI has pre-filled fields it detected in the tender document. Please review and edit before saving.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Participation method *">
              <select value={form.participation_method} onChange={(e) => setForm((f) => ({ ...f, participation_method: e.target.value, dealer_customer_id: '', liaison_customer_id: '' }))} className="input" data-testid="participation-method">
                {PARTICIPATION_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </FormField>
            {form.participation_method === 'Through Dealer' && (
              <FormField label={`Dealer * (must be tagged as "Dealer" in Customers)`}>
                <select value={form.dealer_customer_id} onChange={(e) => setForm((f) => ({ ...f, dealer_customer_id: e.target.value }))} className="input" data-testid="participation-dealer">
                  <option value="">— Select dealer —</option>
                  {dealers.map((c) => <option key={c.id} value={c.id}>{c.organisation_name} · {c.city || c.state || ''}</option>)}
                </select>
                {dealers.length === 0 && <p className="text-[11px] text-rose-600 mt-1">No dealers found. Go to Customers → set a customer&apos;s role to &quot;Dealer&quot;.</p>}
              </FormField>
            )}
            {form.participation_method === 'With Liaison' && (
              <FormField label={`Liaison Agent * (must be tagged as "Liaison Agent" in Customers)`}>
                <select value={form.liaison_customer_id} onChange={(e) => setForm((f) => ({ ...f, liaison_customer_id: e.target.value }))} className="input" data-testid="participation-liaison">
                  <option value="">— Select liaison —</option>
                  {liaisons.map((c) => <option key={c.id} value={c.id}>{c.organisation_name} · {c.city || c.state || ''}</option>)}
                </select>
                {liaisons.length === 0 && <p className="text-[11px] text-rose-600 mt-1">No liaison agents found. Go to Customers → set a customer&apos;s role to &quot;Liaison Agent&quot;.</p>}
              </FormField>
            )}
            <FormField label="Payment terms *">
              <select value={form.payment_terms} onChange={(e) => setForm((f) => ({ ...f, payment_terms: e.target.value }))} className="input" data-testid="participation-payment-terms">
                {PAYMENT_TERMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </FormField>
            <FormField label="Payment terms notes"><input value={form.payment_terms_notes} onChange={(e) => setForm((f) => ({ ...f, payment_terms_notes: e.target.value }))} className="input" placeholder='e.g. "30% advance, 70% on delivery"' /></FormField>
            <FormField label="Delivery terms *">
              <select value={form.delivery_terms} onChange={(e) => setForm((f) => ({ ...f, delivery_terms: e.target.value }))} className="input" data-testid="participation-delivery-terms">
                {DELIVERY_TERMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </FormField>
            <FormField label="Delivery terms notes"><input value={form.delivery_terms_notes} onChange={(e) => setForm((f) => ({ ...f, delivery_terms_notes: e.target.value }))} className="input" /></FormField>
            <FormField label="Delivery mode *">
              <div className="flex gap-4 pt-1.5">
                {DELIVERY_MODES.map((m) => (
                  <label key={m} className="flex items-center gap-1.5 text-sm">
                    <input type="radio" name="delivery_mode" checked={form.delivery_mode === m} onChange={() => setForm((f) => ({ ...f, delivery_mode: m }))} data-testid={`participation-delivery-mode-${m.replace(' ', '-').toLowerCase()}`} />
                    {m}


                  </label>
                ))}
              </div>
            </FormField>
            {form.delivery_mode === 'Multi Location' && (
              <FormField label="Locations (freeform)"><textarea rows={2} value={form.delivery_locations_notes} onChange={(e) => setForm((f) => ({ ...f, delivery_locations_notes: e.target.value }))} className="input" placeholder="One per line: Bangalore, Chennai, Hyderabad …" /></FormField>
            )}
            <FormField label="Bid validity (days)"><input type="number" min={0} value={form.bid_validity_days} onChange={(e) => setForm((f) => ({ ...f, bid_validity_days: parseInt(e.target.value) || 0 }))} className="input" /></FormField>
            <FormField label="Contract duration"><input value={form.contract_duration} onChange={(e) => setForm((f) => ({ ...f, contract_duration: e.target.value }))} className="input" placeholder="e.g. 2 years" /></FormField>
            <FormField label="Warranty period"><input value={form.warranty_period} onChange={(e) => setForm((f) => ({ ...f, warranty_period: e.target.value }))} className="input" placeholder="e.g. 1 year" /></FormField>
            <FormField label="Performance criteria"><input value={form.performance_criteria} onChange={(e) => setForm((f) => ({ ...f, performance_criteria: e.target.value }))} className="input" placeholder="e.g. 99% uptime SLA" /></FormField>
            <FormField label="Experience criteria" className="sm:col-span-2"><input value={form.experience_criteria} onChange={(e) => setForm((f) => ({ ...f, experience_criteria: e.target.value }))} className="input" placeholder="e.g. 3 similar deployments in last 3 years" /></FormField>
            <FormField label="Other relevant info" className="sm:col-span-2"><textarea rows={3} value={form.other_info} onChange={(e) => setForm((f) => ({ ...f, other_info: e.target.value }))} className="input" /></FormField>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-[#E2ECEA]">
            <button onClick={() => setEditing(false)} className="text-sm px-3 py-1.5 border border-[#D5E8E4] rounded text-[#3B5A54]" data-testid="participation-cancel">Cancel</button>
            <button onClick={save} disabled={saving} className="text-sm px-4 py-1.5 bg-[#00a69c] text-white rounded font-semibold hover:bg-[#00bfb3] disabled:opacity-60" data-testid="participation-save">
              {saving ? 'Saving…' : 'Save Participation'}
            </button>
          </div>
          <style>{`.input{width:100%;padding:8px 12px;border:1px solid #D5E8E4;border-radius:8px;font-size:14px;background:white}.input:disabled{background:#F5F8F7;color:#9DB4AF;cursor:not-allowed}`}</style>
        </div>
      )}
    </div>
  );
}

const FormField = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-sm font-medium text-[#3B5A54] mb-1">{label}</span>
    {children}
  </label>
);



/* iter97 — Compact audit trail for Step 2 saves. Renders the most recent
 * event first; expands to full history on click. Never blocks the page. */
const _FIELD_LABELS = {
  participation_method: 'Method',
  dealer_customer_name: 'Dealer',
  liaison_customer_name: 'Liaison Agent',
  payment_terms: 'Payment terms',
  payment_terms_notes: 'Payment notes',
  delivery_terms: 'Delivery terms',
  delivery_terms_notes: 'Delivery notes',
  delivery_mode: 'Delivery mode',
  delivery_locations_notes: 'Locations',
  bid_validity_days: 'Bid validity',
  contract_duration: 'Contract duration',
  warranty_period: 'Warranty',
  performance_criteria: 'Performance criteria',
  experience_criteria: 'Experience criteria',
  participation_other_info: 'Other info',
};
function ParticipationHistory({ history }) {
  const [expanded, setExpanded] = useState(false);
  if (!Array.isArray(history) || history.length === 0) return null;
  const shown = expanded ? history : history.slice(-3);
  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };
  return (
    <div className="mt-4 pt-3 border-t border-[#F0F5F4]" data-testid="tender-participation-history">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold text-[#6D8E87] uppercase tracking-wider">Audit trail · {history.length} event{history.length > 1 ? 's' : ''}</h4>
        {history.length > 3 && (
          <button onClick={() => setExpanded((v) => !v)} className="text-[11px] text-[#00a69c] font-semibold hover:underline" data-testid="participation-history-toggle">
            {expanded ? 'Show latest 3' : `Show all ${history.length}`}
          </button>
        )}
      </div>
      <ol className="relative border-l-2 border-[#E2ECEA] pl-4 space-y-3 ml-1">
        {[...shown].reverse().map((ev, idx) => {
          const changeKeys = Object.keys(ev.changes || {});
          const isCreate = ev.action === 'created';
          return (
            <li key={ev.at + idx} className="relative" data-testid={`participation-history-entry-${idx}`}>
              <span className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full ${isCreate ? 'bg-emerald-500' : 'bg-indigo-500'} ring-2 ring-white`}></span>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xs font-semibold text-[#052A23]">{ev.by_name || 'Unknown'}</span>
                <span className="text-[10px] uppercase tracking-wide text-[#6D8E87]">{ev.by_role || ''}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isCreate ? 'bg-emerald-100 text-emerald-800' : 'bg-indigo-100 text-indigo-800'}`}>
                  {isCreate ? 'CREATED' : 'EDITED'}
                </span>
                <span className="text-[11px] text-[#6D8E87]">{fmt(ev.at)}</span>
              </div>
              {!isCreate && changeKeys.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-[11px] text-[#3B5A54]">
                  {changeKeys.map((k) => (
                    <li key={k}>
                      <span className="text-[#6D8E87]">{_FIELD_LABELS[k] || k}: </span>
                      <span className="line-through text-rose-600">{String(ev.changes[k].from) || '—'}</span>
                      <span className="mx-1 text-[#6D8E87]">→</span>
                      <span className="text-emerald-700 font-medium">{String(ev.changes[k].to) || '—'}</span>
                    </li>
                  ))}
                </ul>
              )}
              {!isCreate && changeKeys.length === 0 && (
                <div className="mt-0.5 text-[11px] text-[#6D8E87] italic">Saved without any field changes.</div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
